import { ReactNode, useEffect, useId, useMemo, useRef, useState } from "react";
import "./index.scss";

// components
import { Checkbox, TablePagination, TableSortLabel } from "@mui/material";
import MUITable from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { Header } from "components/atoms/Typography";

// helpers
import classNames from "classnames";
import MUITheme from "components/MUITheme";
import Tooltip from "components/atoms/Tooltip";
import SearchBar from "components/organisms/SearchBar/SearchBar";
import useTableState from "recoilStore/Table/useTableState";
import { leftAlignedKeys } from "utils/constants";
import { Page } from "utils/enums";
import {
    calculateAlignmentValue,
    cellIsValid,
    getComparator,
    getPaginationPageOptions,
    PAGINATION_OPTIONS,
    splitTitleIntoNameAndUnit,
} from "./functions";

export interface DataSource {
    key: string | number;
    [columnName: string]: any;
}

export interface TableColumn {
    title: string | ReactNode;
    key: string;
    render: any;
    tooltip?: string | ReactNode;
    customSort?: (order: "asc" | "desc") => (a: any, b: any) => number;
    columnClass?: string;
    parentPage?: Page;
}

interface TableProps<T> {
    dataSource: T[];
    selectedRows?: T[];
    setSelectedRows?: ((rows: T[]) => void) | React.Dispatch<React.SetStateAction<T[]>>;
    columns: TableColumn[];
    onRowClick?: (row: T) => void;
    className?: string;
    pagination?: boolean;
    defaultOrderBy?: SortOrder;
    parentComponentWithSearchBarId?: string;
    getRowHref?: (row: T) => string;
}

interface SortOrder {
    key: string;
    order: "asc" | "desc";
}

export function Table<T extends DataSource>({
    dataSource,
    columns,
    onRowClick,
    selectedRows,
    setSelectedRows,
    className,
    pagination = true,
    defaultOrderBy,
    parentComponentWithSearchBarId,
    getRowHref,
}: TableProps<T>) {
    const tableUniqueId = useId();
    const componentId = parentComponentWithSearchBarId || tableUniqueId; // Needed to allow a parent componentId for the portfolio page, depending on the tab a different component is sent
    const [orderBy, setOrderBy] = useState<SortOrder | null>(!defaultOrderBy ? null : defaultOrderBy);
    const tableRef = useRef<HTMLDivElement>(null);
    const [hasScroll, setHasScroll] = useState(false);
    const { searchBarText, tablePage: page, setTablePage: setPage } = useTableState(componentId);

    const defaultRowsPerPage = useMemo(() => {
        if (dataSource.length <= 50) return dataSource.length;
        return 100;
    }, []);

    const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);

    useEffect(() => {
        // if pagination is set to show all, update the rowsPerPage when filtering is applied
        if (!PAGINATION_OPTIONS.includes(rowsPerPage)) {
            setRowsPerPage(dataSource.length);
        }
        setPage(0);
    }, [dataSource]);

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const visibleRows = useMemo(
        () =>
            dataSource
                .filter((row) => {
                    if (!searchBarText) return true;
                    return columns.some((column) => {
                        const cellValue = row[column.key];
                        return cellValue?.toString().toLowerCase().includes(searchBarText.toLowerCase());
                    });
                })
                .sort((a, b) => {
                    if (!orderBy) return 0;
                    const column = columns.find((column) => column.key === orderBy.key);
                    if (column && column.customSort) return column.customSort(orderBy.order)(a, b);
                    return orderBy ? getComparator(orderBy.order, orderBy.key)(a, b) : 0;
                })
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [page, rowsPerPage, orderBy, dataSource, searchBarText]
    );

    const rowCount = useMemo(() => dataSource.length, [visibleRows]);

    const handleSelectionChange = (newSelection: T[]) => {
        if (!setSelectedRows || !selectedRows) return;

        if (typeof setSelectedRows === "function") {
            setSelectedRows(newSelection);
        }
    };

    const onSelectAllClick = () => {
        if (!setSelectedRows || !selectedRows) return;
        const newSelection = selectedRows.length === dataSource.length ? [] : dataSource;
        handleSelectionChange(newSelection);
    };

    const numSelected = useMemo(() => selectedRows?.length || 0, [selectedRows]);

    const handleCheckboxChange = (row: T) => (event: any, checked: boolean) => {
        if (!setSelectedRows || !selectedRows) return;

        const newSelection = checked ? [...selectedRows, row] : selectedRows.filter((r) => r.key !== row.key);

        handleSelectionChange(newSelection);
    };
    useEffect(() => {
        const checkScroll = () => {
            if (tableRef.current) {
                const { scrollHeight, clientHeight, scrollTop } = tableRef.current;
                const isScrollable = scrollHeight > clientHeight;
                const isAtBottom = scrollHeight - scrollTop <= clientHeight + 5;
                setHasScroll(isScrollable && !isAtBottom);
            }
        };

        checkScroll();
        const container = tableRef.current;
        container?.addEventListener("scroll", checkScroll);
        window.addEventListener("resize", checkScroll);

        return () => {
            container?.removeEventListener("scroll", checkScroll);
            window.removeEventListener("resize", checkScroll);
        };
    }, [visibleRows]);
    return (
        <div className={classNames("table-pagination-wrapper", className && className)}>
            {!parentComponentWithSearchBarId && (
                <div style={{ marginRight: "auto", paddingLeft: "5px" }}>
                    <SearchBar tableId={componentId} />
                </div>
            )}
            <MUITheme primaryColor="#eb03ad">
                <TableContainer
                    ref={tableRef}
                    className={classNames("table", hasScroll && "has-scroll", "scroll-shadows")}
                >
                    {" "}
                    <MUITable sx={{ minWidth: 650 }} aria-label="simple table">
                        <TableHead
                            style={{
                                position: "sticky",
                                top: 0,
                                left: 0,
                                zIndex: 1,
                                // [2025-09-19 TRC] I made a brief attempt to make hella long column names wrap, but it doesn't work.  Please someone revisit when you have a chance.
                                maxWidth: 30,
                                maxInlineSize: 30,
                                textWrap: "pretty",
                            }}
                        >
                            <TableRow
                                style={{
                                    backgroundColor: "var(--audette-gray-100)",
                                    border: "none",
                                }}
                                tabIndex={-1}
                                role="checkbox"
                            >
                                {/* Update the header */}
                                {selectedRows && (
                                    <TableCell
                                        padding="checkbox"
                                        style={{
                                            borderBottom: "none",
                                        }}
                                    >
                                        <Checkbox
                                            color="primary"
                                            indeterminate={numSelected > 0 && numSelected < rowCount}
                                            checked={rowCount > 0 && numSelected === rowCount}
                                            onChange={onSelectAllClick}
                                        />
                                    </TableCell>
                                )}
                                {columns.map(({ title, key, tooltip, columnClass }) => (
                                    <TableCell
                                        key={key}
                                        align={leftAlignedKeys.includes(key) ? "left" : "right"}
                                        className={classNames(
                                            columnClass,
                                            key === "buildingModelUid" || key === "cashFlowAnnualHidden"
                                                ? "hidden-column"
                                                : ""
                                        )}
                                        sortDirection={orderBy?.key === key ? orderBy.order : false}
                                        style={{
                                            borderBottom: "none",
                                        }}
                                    >
                                        <TableSortLabel
                                            active={orderBy?.key === key}
                                            direction={orderBy?.order}
                                            onClick={() => {
                                                setOrderBy((prev) => {
                                                    if (prev === null)
                                                        return {
                                                            order: "asc",
                                                            key,
                                                        };
                                                    let order: "asc" | "desc" = "asc";
                                                    if (prev?.key === key)
                                                        order = prev?.order === "asc" ? "desc" : "asc";
                                                    return {
                                                        key,
                                                        order,
                                                    };
                                                });
                                            }}
                                        >
                                            {tooltip && (
                                                <Tooltip
                                                    style={{
                                                        marginLeft: "8px",
                                                    }}
                                                    tooltipBody={tooltip}
                                                />
                                            )}
                                            <div>
                                                <Header
                                                    size="x-small"
                                                    style={{
                                                        lineHeight: "20px",
                                                        color: "var(--audette-gray-600)",
                                                        width: "max-content",
                                                    }}
                                                >
                                                    {typeof title === "string" ? (
                                                        <>
                                                            {splitTitleIntoNameAndUnit(title).name}
                                                            <br />
                                                            {splitTitleIntoNameAndUnit(title).unit}
                                                        </>
                                                    ) : (
                                                        title
                                                    )}
                                                </Header>
                                            </div>
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visibleRows.map((row, rowIndex) => (
                                <TableRow
                                    key={row.key}
                                    sx={{
                                        "&:last-child td, &:last-child th": {
                                            border: 0,
                                        },
                                    }}
                                    component={getRowHref ? "a" : "tr"}
                                    style={getRowHref ? { textDecoration: "none" } : undefined}
                                    href={getRowHref ? getRowHref(row) : undefined}
                                    onClick={
                                        onRowClick
                                            ? (event: any) => {
                                                  if (
                                                      event.ctrlKey ||
                                                      event.metaKey ||
                                                      event.shiftKey ||
                                                      event.button !== 0
                                                  ) {
                                                      return;
                                                  }
                                                  if (getRowHref) {
                                                      event.preventDefault();
                                                  }
                                                  onRowClick(row);
                                              }
                                            : undefined
                                    }
                                    className={classNames(onRowClick && "table-row")}
                                >
                                    {selectedRows && (
                                        <TableCell
                                            padding="checkbox"
                                            style={{
                                                borderBottom: "1px solid var(--audette-gray-100)",
                                            }}
                                        >
                                            <Checkbox
                                                onClick={(e) => e.stopPropagation()}
                                                color="primary"
                                                checked={selectedRows?.findIndex((r) => row.key === r.key) !== -1}
                                                onChange={handleCheckboxChange(row)}
                                            />
                                        </TableCell>
                                    )}

                                    {/* Update the body */}
                                    {columns.map(({ key, render, columnClass }) => (
                                        <TableCell
                                            align={calculateAlignmentValue(row[key])}
                                            className={classNames(
                                                columnClass,
                                                key === "buildingModelUid" ? "hidden-column" : ""
                                            )}
                                            key={`${row.key}$${key}`}
                                            sortDirection={orderBy?.key === key ? orderBy.order : false}
                                            style={{
                                                borderBottom: "1px solid var(--audette-gray-100)",
                                            }}
                                        >
                                            <span>{cellIsValid(row[key]) ? render(row[key], rowIndex, row) : ""}</span>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </MUITable>
                </TableContainer>
                {pagination && (
                    <TablePagination
                        className="table-pagination"
                        rowsPerPageOptions={getPaginationPageOptions(dataSource.length)}
                        component="div"
                        count={dataSource.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(_, p) => {
                            setPage(p);
                        }}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                )}
            </MUITheme>
        </div>
    );
}
